import type { UIEvent } from "react";
import { Fragment, useEffect, useRef } from "react";
import { createUseStyles } from "react-jss";
import type { AgreementDocument } from "../../../../common/src";
import type { VetBoatsTheme } from "../../theme";

const useStyles = createUseStyles((theme: VetBoatsTheme) => ({
  scroller: {
    maxHeight: "min(60vh, 560px)",
    overflowY: "auto",
    padding: "6px 18px 14px",
    border: `1px solid ${theme.color.border}`,
    borderRadius: 6,
    background: theme.color.surface,
    lineHeight: 1.55,
    fontSize: 15,
    "&:focus-visible": { outline: `2px solid ${theme.color.accent}` },
  },
  open: { maxHeight: "none", overflowY: "visible" },
  title: { fontSize: 19, margin: "12px 0 2px" },
  version: { color: theme.color.muted, fontSize: 13, margin: "0 0 12px" },
  notice: {
    fontWeight: 700,
    background: theme.color.dangerBg,
    padding: "10px 12px",
    borderRadius: 6,
  },
  facts: {
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    gap: "4px 14px",
    margin: "14px 0",
    padding: 12,
    background: theme.color.background,
    borderRadius: 6,
    "& dt": { color: theme.color.muted },
    "& dd": { margin: 0, fontWeight: 600 },
  },
  heading: { fontSize: 16, margin: "18px 0 6px" },
  paragraph: { margin: "0 0 10px" },
  strong: { fontWeight: 700 },
  list: { margin: "0 0 10px", paddingLeft: 22 },
}));

interface AgreementViewProps {
  doc: AgreementDocument;
  /** Called once the reader has reached the end of the text. */
  onReadToEnd?: () => void;
  /** Show the whole text without its own scroll box (for read-only pages). */
  expanded?: boolean;
}

/** An agreement exactly as signed: the same structure the server fingerprints. */
export const AgreementView = ({
  doc,
  onReadToEnd,
  expanded = false,
}: AgreementViewProps) => {
  const classes = useStyles();
  const box = useRef<HTMLDivElement>(null);

  const checkEnd = (el: HTMLElement): void => {
    if (onReadToEnd && el.scrollTop + el.clientHeight >= el.scrollHeight - 12) {
      onReadToEnd();
    }
  };

  // Text short enough to fit without scrolling counts as read.
  useEffect(() => {
    if (box.current && !expanded) {
      checkEnd(box.current);
    }
  });

  return (
    <div
      ref={box}
      className={`${classes.scroller} ${expanded ? classes.open : ""}`}
      onScroll={(e: UIEvent<HTMLDivElement>) => checkEnd(e.currentTarget)}
      tabIndex={0}
      aria-label={`${doc.title}, full text`}
    >
      <h2 className={classes.title}>{doc.title}</h2>
      <p className={classes.version}>Version: {doc.version}</p>
      <p className={classes.notice}>{doc.notice}</p>
      {doc.facts.length > 0 && (
        <dl className={classes.facts}>
          {doc.facts.map((f) => (
            <Fragment key={f.label}>
              <dt>{f.label}</dt>
              <dd>{f.value}</dd>
            </Fragment>
          ))}
        </dl>
      )}
      {doc.sections.map((section) => (
        <section key={section.heading}>
          <h3 className={classes.heading}>{section.heading}</h3>
          {section.blocks.map((block, i) =>
            block.type === "paragraph" ? (
              <p
                key={i}
                className={`${classes.paragraph} ${block.strong ? classes.strong : ""}`}
              >
                {block.text}
              </p>
            ) : (
              <ul key={i} className={classes.list}>
                {block.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            )
          )}
        </section>
      ))}
    </div>
  );
};
